import axiosInstance from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { ApiResponse } from 'types';
import { dateStringToEpoch, dateStringToEpochEndOfDay, getCurrentEpoch } from 'utils/helpers';
import { localDayKey } from 'pages/dashboardScope';

// Backend report structure (matches actual API response - snake_case)
export interface DailyReport {
  id: number;
  business_id: string;
  day_start: string;
  day_end: string;
  total_calls: number;
  total_ai_calls: number;
  total_inbound_calls: number;
  total_outbound_calls: number;
  total_transferred_calls: number;
  total_ai_transferred_calls: number;
  total_billed_time_ms: number;
  total_ai_session_time_ms: number;
  total_answered_calls: number;
  total_missed_calls: number;
  avg_call_duration_seconds: number;
  unique_dialed_numbers: number;
}

export interface HourlyReport {
  id: number;
  business_id: string;
  hour_start: string;
  hour_end: string;
  total_calls: number;
  total_ai_calls: number;
  total_inbound_calls: number;
  total_outbound_calls: number;
  total_transferred_calls: number;
  total_ai_transferred_calls: number;
  total_billed_time_ms: number;
  total_ai_session_time_ms: number;
  total_answered_calls: number;
  total_missed_calls: number;
  avg_call_duration_seconds: number;
  unique_dialed_numbers: number;
  peak_minute?: number;
}

// Frontend display structure
export interface DashboardStats {
  totalCalls: number;
  incomingCalls: number;
  outgoingCalls: number;
  answeredCalls: number;
  missedCalls: number;
  /**
   * Average call length in SECONDS.
   *
   * Computed here as totalDurationSeconds / totalCalls, where
   * totalDurationSeconds is itself summed from the reports'
   * `avg_call_duration_seconds`. Every consumer treated it as MILLISECONDS —
   * dividing by 1000 or by 60000 — so the dashboards' duration tiles read 1000x
   * low and showed "0 minutes" against 53,909 real calls. Units named here
   * because the field name cannot carry them.
   */
  avgDurationSeconds: number;
  activeSessions: number;
  totalTransfers: number;
  recordings: number;
  // New fields from reports
  totalAiCalls?: number;
  totalAiTransferredCalls?: number;
  totalBilledTimeMs?: number;
  totalAiSessionTimeMs?: number;
  uniqueDialedNumbers?: number;
}

export interface DailyChartData {
  date: string;
  day: string;
  calls: number;
  incoming: number;
  outgoing: number;
  missed?: number;
  totalAiCalls?: number;
  totalAiTransferredCalls?: number;
  totalTransfers?: number;
  totalAiSessionTimeMs?: number;
}

export interface HourlyChartData {
  hour: number;
  hourLabel: string;
  calls: number;
  incoming: number;
  outgoing: number;
  missed?: number;
  totalAiCalls?: number;
  totalAiTransferredCalls?: number;
  totalTransfers?: number;
  totalAiSessionTimeMs?: number;
}

export interface AggregatedStatsResponse {
  stats: DashboardStats;
  dailyData: DailyChartData[];
  pieData: { name: string; value: number }[];
}

export interface DashboardFilterParams {
  dateFrom?: string;
  dateTo?: string;
  organizationId?: string;
}

/**
 * Transform daily reports array to aggregated dashboard response
 */
function transformDailyReports(reports: DailyReport[]): AggregatedStatsResponse {
  // Aggregate totals
  const totals = reports.reduce((acc, report) => ({
    totalCalls: acc.totalCalls + report.total_calls,
    totalAiCalls: acc.totalAiCalls + report.total_ai_calls,
    totalInbound: acc.totalInbound + report.total_inbound_calls,
    totalOutbound: acc.totalOutbound + report.total_outbound_calls,
    totalAnswered: acc.totalAnswered + report.total_answered_calls,
    totalMissed: acc.totalMissed + report.total_missed_calls,
    totalTransfers: acc.totalTransfers + report.total_transferred_calls,
    totalAiTransfers: acc.totalAiTransfers + report.total_ai_transferred_calls,
    totalBilledTimeMs: acc.totalBilledTimeMs + report.total_billed_time_ms,
    totalAiSessionTimeMs: acc.totalAiSessionTimeMs + report.total_ai_session_time_ms,
    totalDurationSeconds: acc.totalDurationSeconds + (report.avg_call_duration_seconds * report.total_calls),
    uniqueNumbers: Math.max(acc.uniqueNumbers, report.unique_dialed_numbers)
  }), {
    totalCalls: 0,
    totalAiCalls: 0,
    totalInbound: 0,
    totalOutbound: 0,
    totalAnswered: 0,
    totalMissed: 0,
    totalTransfers: 0,
    totalAiTransfers: 0,
    totalBilledTimeMs: 0,
    totalAiSessionTimeMs: 0,
    totalDurationSeconds: 0,
    uniqueNumbers: 0
  });

  // Build stats
  const stats: DashboardStats = {
    totalCalls: totals.totalCalls,
    incomingCalls: totals.totalInbound,
    outgoingCalls: totals.totalOutbound,
    answeredCalls: totals.totalAnswered,
    missedCalls: totals.totalMissed,
    avgDurationSeconds: totals.totalCalls > 0 ? Math.floor(totals.totalDurationSeconds / totals.totalCalls) : 0,
    activeSessions: totals.totalAiCalls,
    totalTransfers: totals.totalTransfers,
    recordings: 0,
    totalAiCalls: totals.totalAiCalls,
    totalAiTransferredCalls: totals.totalAiTransfers,
    totalBilledTimeMs: totals.totalBilledTimeMs,
    totalAiSessionTimeMs: totals.totalAiSessionTimeMs,
    uniqueDialedNumbers: totals.uniqueNumbers
  };

  // Build chart data
  const dailyData: DailyChartData[] = reports.map(report => ({
    date: report.day_start,
    day: new Date(report.day_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    calls: report.total_calls,
    incoming: report.total_inbound_calls,
    outgoing: report.total_outbound_calls,
    totalAiCalls: report.total_ai_calls,
    totalAiTransferredCalls: report.total_ai_transferred_calls,
    totalTransfers: report.total_transferred_calls,
    missed: report.total_missed_calls,
    totalAiSessionTimeMs: report.total_ai_session_time_ms
  }));

  // Build pie data
  const pieData = [
    { name: 'Incoming', value: totals.totalInbound },
    { name: 'Outgoing', value: totals.totalOutbound }
  ];

  return { stats, dailyData, pieData };
}

/**
 * Transform hourly reports array to aggregated dashboard response
 */
function transformHourlyReports(reports: HourlyReport[]): AggregatedStatsResponse {
  // Aggregate totals
  const totals = reports.reduce((acc, report) => ({
    totalCalls: acc.totalCalls + report.total_calls,
    totalAiCalls: acc.totalAiCalls + report.total_ai_calls,
    totalInbound: acc.totalInbound + report.total_inbound_calls,
    totalOutbound: acc.totalOutbound + report.total_outbound_calls,
    totalAnswered: acc.totalAnswered + report.total_answered_calls,
    totalMissed: acc.totalMissed + report.total_missed_calls,
    totalTransfers: acc.totalTransfers + report.total_transferred_calls,
    totalAiTransfers: acc.totalAiTransfers + report.total_ai_transferred_calls,
    totalBilledTimeMs: acc.totalBilledTimeMs + report.total_billed_time_ms,
    totalAiSessionTimeMs: acc.totalAiSessionTimeMs + report.total_ai_session_time_ms,
    totalDurationSeconds: acc.totalDurationSeconds + (report.avg_call_duration_seconds * report.total_calls),
    uniqueNumbers: Math.max(acc.uniqueNumbers, report.unique_dialed_numbers)
  }), {
    totalCalls: 0,
    totalAiCalls: 0,
    totalInbound: 0,
    totalOutbound: 0,
    totalAnswered: 0,
    totalMissed: 0,
    totalTransfers: 0,
    totalAiTransfers: 0,
    totalBilledTimeMs: 0,
    totalAiSessionTimeMs: 0,
    totalDurationSeconds: 0,
    uniqueNumbers: 0
  });

  // Build stats
  const stats: DashboardStats = {
    totalCalls: totals.totalCalls,
    incomingCalls: totals.totalInbound,
    outgoingCalls: totals.totalOutbound,
    answeredCalls: totals.totalAnswered,
    missedCalls: totals.totalMissed,
    avgDurationSeconds: totals.totalCalls > 0 ? Math.floor(totals.totalDurationSeconds / totals.totalCalls) : 0,
    activeSessions: totals.totalAiCalls,
    totalTransfers: totals.totalTransfers,
    recordings: 0,
    totalAiCalls: totals.totalAiCalls,
    totalAiTransferredCalls: totals.totalAiTransfers,
    totalBilledTimeMs: totals.totalBilledTimeMs,
    totalAiSessionTimeMs: totals.totalAiSessionTimeMs,
    uniqueDialedNumbers: totals.uniqueNumbers
  };

  // Helper function to ensure UTC timestamps are properly converted to local time
  const parseUTCTimestamp = (timestamp: string): Date => {
    // If timestamp doesn't have timezone indicator, treat it as UTC
    if (typeof timestamp === 'string' &&
        !timestamp.includes('Z') &&
        !timestamp.includes('+') &&
        !timestamp.includes('-', 10)) {
      // Add 'Z' to force UTC parsing for timestamps like "2025-10-07T14:00:00"
      return new Date(timestamp + 'Z');
    }
    return new Date(timestamp);
  };

  // Build hourly chart data in 24-hour format
  const dailyData: DailyChartData[] = reports.map(report => {
    const hourDate = parseUTCTimestamp(report.hour_start);
    const hour = hourDate.getHours(); // Returns hour in local timezone
    const hourLabel = hourDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

    return {
      date: report.hour_start,
      day: hourLabel,
      calls: report.total_calls,
      incoming: report.total_inbound_calls,
      outgoing: report.total_outbound_calls,
      totalAiCalls: report.total_ai_calls,
      totalAiTransferredCalls: report.total_ai_transferred_calls,
      totalTransfers: report.total_transferred_calls,
      missed: report.total_missed_calls,
      totalAiSessionTimeMs: report.total_ai_session_time_ms
    };
  });

  // Build pie data
  const pieData = [
    { name: 'Incoming', value: totals.totalInbound },
    { name: 'Outgoing', value: totals.totalOutbound }
  ];

  return { stats, dailyData, pieData };
}

export const dashboardService = {
  /**
   * Get dashboard stats aggregated by day
   * Use this when date range is more than 1 day or for weekly/monthly views
   */
  async getDailyStats(params?: DashboardFilterParams): Promise<ApiResponse<AggregatedStatsResponse>> {
    try {
      // Convert date strings to epoch timestamps (milliseconds)
      const apiParams: any = {
        organizationId: params?.organizationId
      };

      if (params?.dateFrom) {
        // Convert to start of day epoch
        apiParams.fromDateEpoch = dateStringToEpoch(params.dateFrom);
      }

      if (params?.dateTo) {
        // For 'to' date, use end of day epoch
        apiParams.toDateEpoch = dateStringToEpochEndOfDay(params.dateTo);
      }

      console.log('📅 Daily API - Date conversion:', {
        input: {
          fromDate: params?.dateFrom,
          toDate: params?.dateTo
        },
        output: {
          fromDateEpoch: apiParams.fromDateEpoch,
          toDateEpoch: apiParams.toDateEpoch
        },
        verification: {
          fromDateAsDate: apiParams.fromDateEpoch ? new Date(apiParams.fromDateEpoch).toISOString() : 'N/A',
          toDateAsDate: apiParams.toDateEpoch ? new Date(apiParams.toDateEpoch).toISOString() : 'N/A'
        }
      });

      const response = await axiosInstance.get<ApiResponse<{ dailyReports: DailyReport[] }>>(
        API_ENDPOINTS.REPORTS_DAILY,
        { params: apiParams }
      );

      if (response.data.success && response.data.data) {
        const reports = response.data.data.dailyReports || [];
        const transformed = transformDailyReports(reports);

        return {
          success: true,
          data: transformed
        };
      }

      return {
        success: false,
        error: response.data.error || 'Failed to fetch daily stats'
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch daily stats'
      };
    }
  },

  /**
   * Get dashboard stats aggregated by hour
   * Use this when date range is within a single day for detailed hourly view
   */
  async getHourlyStats(params?: DashboardFilterParams): Promise<ApiResponse<AggregatedStatsResponse>> {
    try {
      // Convert date strings to epoch timestamps (milliseconds)
      const apiParams: any = {
        organizationId: params?.organizationId
      };

      if (params?.dateFrom) {
        // Convert to start of day epoch
        apiParams.fromDateTimeEpoch = dateStringToEpoch(params.dateFrom);
      }

      if (params?.dateTo) {
        // Check if toDate is today - if so, use current time, otherwise use the provided datetime
        const today = localDayKey(new Date()); // the viewer's (IST) today, not UTC's
        const toDatePart = params.dateTo.split('T')[0]; // Extract date part from datetime string

        if (toDatePart === today) {
          // If datetime string specifies a time, use that; otherwise use current time
          if (params.dateTo.includes('T')) {
            // User specified a datetime, use it
            apiParams.toDateTimeEpoch = dateStringToEpoch(params.dateTo);
          } else {
            // User only specified date, use current time
            apiParams.toDateTimeEpoch = getCurrentEpoch();
          }
        } else {
          // Use the specified datetime or end of day if only date provided
          apiParams.toDateTimeEpoch = dateStringToEpoch(params.dateTo);
        }
      }

      console.log('📅 Hourly API - Date conversion:', {
        input: {
          fromDate: params?.dateFrom,
          toDate: params?.dateTo,
          isToday: params?.dateTo ? params.dateTo.split('T')[0] === localDayKey(new Date()) : false
        },
        output: {
          fromDateTimeEpoch: apiParams.fromDateTimeEpoch,
          toDateTimeEpoch: apiParams.toDateTimeEpoch
        },
        verification: {
          fromDateAsDate: apiParams.fromDateTimeEpoch ? new Date(apiParams.fromDateTimeEpoch).toISOString() : 'N/A',
          toDateAsDate: apiParams.toDateTimeEpoch ? new Date(apiParams.toDateTimeEpoch).toISOString() : 'N/A'
        }
      });

      const response = await axiosInstance.get<ApiResponse<{ hourlyReports: HourlyReport[] }>>(
        API_ENDPOINTS.REPORTS_HOURLY,
        { params: apiParams }
      );

      if (response.data.success && response.data.data) {
        const reports = response.data.data.hourlyReports || [];
        const transformed = transformHourlyReports(reports);

        return {
          success: true,
          data: transformed
        };
      }

      return {
        success: false,
        error: response.data.error || 'Failed to fetch hourly stats'
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch hourly stats'
      };
    }
  },

  /**
   * Get basic dashboard stats without detailed breakdown
   * Use this for quick overview without charts
   */
  async getBasicStats(params?: DashboardFilterParams): Promise<ApiResponse<DashboardStats>> {
    try {
      const response = await axiosInstance.get<ApiResponse<DashboardStats>>(
        API_ENDPOINTS.DASHBOARD_STATS,
        { params }
      );

      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch dashboard stats'
      };
    }
  }
};
